import axios from 'axios'
import Vue from 'vue'
import _ from 'lodash'

export default {
  namespaced: true,
  state: {
    files: [],
    folders: []
  },

  mutations: {
    setFiles (state, files) {
      state.files = files
    },

    setFolders (state, folders) {
      state.folders = folders
    },

    addFile (state, file) {
      state.files.push(file)
    },

    addFolder (state, folder) {
      state.folders.push(folder)
    },

    updateFile (state, file) {
      const { id: fileId } = file
      const fileIndex = state.files.findIndex(file => file.id === fileId)

      if (fileIndex >= 0) {
        const updatedFile = { ...state.files[fileIndex] }

        updatedFile.queryName = file.queryName
        updatedFile.queryText = file.queryText
        updatedFile.folderId = file.folderId
        Vue.set(state.files, fileIndex, updatedFile)
      }
    },

    removeFile (state, fileId) {
      const fileIndex = state.files.findIndex(file => file.id === fileId)

      if (fileIndex >= 0) {
        state.files.splice(fileIndex, 1)
      }
    },

    removeFolder (state, folderId) {
      const folderIndex = state.folders.findIndex(folder => folder.id === folderId)

      if (folderIndex >= 0) {
        state.folders.splice(folderIndex, 1)
      }
    }
  },

  getters: {
    queryRepo (state) {
      const { files, folders } = state

      return {
        files,
        folders
      }
    }
  },

  actions: {
    /**
     * Fetches the Query Repo
     */
    async fetchQueryRepo ({ commit }) {
      try {
        const queryRepo = (await axios.get(`${process.env.VUE_APP_API_URI}/query-pad/query-repo`)).data
        const { files, folders } = queryRepo

        commit('setFiles', files)
        commit('setFolders', folders)
      } catch (error) {
        console.error(`Error while fetching Query Repository: ${error}`)
      }
    },

    /**
     * Adds a Query File to the Query Repo
     * Contents of payload:
     * @param {Object} file - Object representation of the file being added
     * @param {Number} folderId - The id of the folder in which the file is saved under
     * @param {Number} userId - The id of the user saving the file
     * @return {Promise} - Resolves to the added file
     */
    async addQueryRepoFile ({ commit }, { file, folderId, userId }) {
      try {
        const { queryName, queryText } = file
        const addedFile = (await axios({
          method: 'post',
          url: `${process.env.VUE_APP_API_URI}/query-pad/query-repo/file`,
          data: {
            queryName,
            queryText,
            folderId,
            userId
          }
        })).data[0]

        commit('addFile', addedFile)

        return addedFile
      } catch (error) {
        console.error(`Error while adding Query Repo File: ${error}`)
        throw error
      }
    },

    /**
     * Updates a Query File in the Query Repo
     * Contents of payload:
     * @param {Object} origFile - The file being updated
     * @param {Object} newFile - The file object with updated fields
     * @param {Number} userId - The id of the user updating the file
     */
    async updateQueryRepoFile ({ commit }, { origFile, newFile, userId }) {
      try {
        const { id, queryName, queryText, folderId } = origFile
        const queryToSave = {
          id,
          queryName: newFile.queryName || queryName,
          queryText: newFile.queryText || queryText,
          folderId: newFile.folderId || folderId
        }
        await axios({
          method: 'put',
          url: `${process.env.VUE_APP_API_URI}/query-pad/query-repo/file`,
          data: {
            queryId: queryToSave.id,
            queryName: queryToSave.queryName,
            queryText: queryToSave.queryText,
            folderId: queryToSave.folderId,
            userId
          }
        })

        commit('updateFile', queryToSave)
      } catch (error) {
        console.error(`Error while updating Query Repo File: ${error}`)
        throw error
      }
    },

    /**
     * Removes the file from Query Repo
     * Contents of payload:
     * @param {Object} file - File object
     * @param {Number} userId - Id of user that is deleting the file
     */
    async removeQueryRepoFile ({ commit }, { file, userId }) {
      try {
        await axios({
          method: 'delete',
          url: `${process.env.VUE_APP_API_URI}/query-pad/query-repo/file`,
          data: {
            queryId: file.id,
            userId
          }
        })

        commit('removeFile', file.id)
      } catch (error) {
        let errorToThrow = error

        if (_.has(error, 'response.data.message')) {
          errorToThrow = new Error(error.response.data.message)
        }

        console.error(`Error while deleting Query Repo File: ${errorToThrow}`)

        throw errorToThrow
      }
    },

    /**
     * Adds a folder to Query Repo
     * Contents of payload:
     * @param {object} folder - The folder to be added
     * @param {Number} parentId - The id of the parent folder
     * @param {Number} userId - The id of the user saving the folder
     */
    async addQueryRepoFolder ({ state, commit }, { folder, parentId, userId }) {
      try {
        const { folders } = state
        const { name, insertable = true, editable = true } = folder
        const parentFolder = folders.find(folder => folder.id === parentId)

        if (parentFolder) {
          const addedFolder = (await axios({
            method: 'post',
            url: `${process.env.VUE_APP_API_URI}/query-pad/query-repo/folder`,
            data: {
              name,
              insertable,
              editable,
              parentId,
              userId
            }
          })).data[0]

          commit('addFolder', addedFolder)
        } else {
          throw new Error('Parent Folder does not exist!')
        }
      } catch (error) {
        console.error(`Error while adding Query Repo Folder: ${error}`)
        throw error
      }
    },

    /**
     * Removes a folder from Query Repo
     * Contents of payload:
     * @param {Object} folder - The folder being deleted
     * @param {Number} userId - The id of the user deleting the folder
     */
    async removeQueryRepoFolder ({ commit }, { folder, userId }) {
      const { id, editable, userId: folderUserId } = folder

      if (editable && (!folderUserId || folderUserId === userId)) {
        try {
          await axios({
            method: 'delete',
            url: `${process.env.VUE_APP_API_URI}/query-pad/query-repo/folder`,
            data: {
              folderId: id,
              userId
            }
          })

          commit('removeFolder', folder.id)
        } catch (error) {
          console.error(`Error while deleting Query Repo Folder: ${error}`)
          throw error
        }
      } else {
        throw new Error('You do not have permissions to delete this folder!')
      }
    },

    async createQueryRepoUserFolder ({ state, commit }, { userEmail, userId }) {
      try {
        const addedFolder = (await axios({
          method: 'post',
          url: `${process.env.VUE_APP_API_URI}/query-pad/query-repo/user-folder`,
          data: { userId, userEmail }
        })).data

        const userFolder = state.folders.find(folder => folder.id === addedFolder.id)

        if (userFolder) {
          return userFolder
        } else {
          commit('addFolder', addedFolder)

          return addedFolder
        }
      } catch (error) {
        console.error(`Error while creating Query Repo Folder: ${error}`)
        throw error
      }
    }
  }
}
